import React, { useEffect, useMemo, useState } from 'react';
import { schedulePersistClientSettings } from '../context/userSettingsSync';

// Default style preferences matching current chart visuals
const defaultPrefs = {
  candle: {
    upColor: '#16a34a',
    downColor: '#dc2626',
    wickUpColor: '#16a34a',
    wickDownColor: '#dc2626',
  },
  sma: {
    '9': { color: '#8fe5af', width: 2, alpha: 1 },
    '20': { color: '#22c55e', width: 2, alpha: 1 },
    '50': { color: '#16a34a', width: 2, alpha: 1 },
    '100': { color: '#059669', width: 2, alpha: 1 },
    '200': { color: '#065f46', width: 2, alpha: 1 },
  },
  ema: {
    '9': { color: '#1316db', width: 2, alpha: 1 },
    '20': { color: '#4caf50', width: 2, alpha: 1 },
    '50': { color: '#3179f5', width: 2, alpha: 1 },
    '100': { color: '#b22833', width: 2, alpha: 1 },
    '200': { color: '#4dd0e1', width: 2, alpha: 1 },
  },
  kama: {
    '9': { color: '#f1a6cd', width: 2, alpha: 1 },
    '20': { color: '#f472b6', width: 2, alpha: 1 },
    '50': { color: '#ec4899', width: 2, alpha: 1 },
    '100': { color: '#db2777', width: 2, alpha: 1 },
    '200': { color: '#be185d', width: 2, alpha: 1 },
  },
  bb: {
    upper: { color: '#f87171', width: 2, alpha: 1 },
    middle: { color: '#9ca3af', width: 2, alpha: 1 },
    lower: { color: '#60a5fa', width: 2, alpha: 1 },
  },
  ichimoku: {
    a: { color: '#10b981', width: 2, alpha: 1 },
    b: { color: '#ef4444', width: 2, alpha: 1 },
    cloudAlpha: 0.18,
  },
  rsi: {
    line: { color: '#f59e0b', width: 2, alpha: 1 },
    guide: { color: '#94a3b8', width: 1, alpha: 0.7 },
  },
  macd: {
    macdLine: { color: '#3b82f6', width: 2, alpha: 1 },
    signalLine: { color: '#ef4444', width: 2, alpha: 1 },
    histUp: { color: '#10b981', alpha: 0.6 },
    histDown: { color: '#ef4444', alpha: 0.6 },
    zeroLine: { color: '#94a3b8', width: 1, alpha: 0.7 },
  },
  adx: {
    adx: { color: '#f59e0b', width: 2, alpha: 1 },
    plus: { color: '#16a34a', width: 1.5, alpha: 1 },
    minus: { color: '#dc2626', width: 1.5, alpha: 1 },
    guide25: { color: '#94a3b8', width: 1, alpha: 0.7 },
  },
  prediction: {
    line: { color: '#f59e0b', width: 2, alpha: 1 },
    hoverLine: { color: '#f59e0b', width: 2, alpha: 1 },
  },
  rsib: {
    lower: { color: '#60a5fa', width: 1, alpha: 1 },
    mid: { color: '#9ca3af', width: 1, alpha: 1 },
    upper: { color: '#f87171', width: 1, alpha: 1 },
  },
  trade: {
    // Use hex + alpha to make native color input work; RGBA is not supported by <input type="color">
    buy: { color: '#79b68f', alpha: 1 },
    sell: { color: '#b66767', alpha: 1 },
    width: 7,
  },
  div: {
    hidBull: { color: '#16a34a', alpha: 0.6 },
    hidBear: { color: '#ef4444', alpha: 0.6 },
    regBull: { color: '#15803d', alpha: 0.6 },
    regBear: { color: '#b91c1c', alpha: 0.6 },
    knox: { color: '#2563eb', alpha: 0.8 },
  },
  dvl: {
    hidBull: { color: '#83ae29', alpha: 0.7 },
    hidBear: { color: '#ea6710', alpha: 0.7 },
    regBull: { color: '#07f2cb', alpha: 0.9 },
    regBear: { color: '#fa0000', alpha: 0.9 },
  },
};

const deepMerge = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const out = Array.isArray(a) ? [...a] : { ...a };
  for (const k of Object.keys(b)) {
    if (b[k] && typeof b[k] === 'object' && !Array.isArray(b[k])) {
      out[k] = deepMerge(a[k], b[k]);
    } else {
      out[k] = b[k];
    }
  }
  return out;
};

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem('borsai.indicatorStylePrefs.v1');
    if (!raw) return defaultPrefs;
    const parsed = JSON.parse(raw);
    const merged = deepMerge(defaultPrefs, parsed);
    // Normalize legacy RGBA strings for trade colors to hex + alpha for proper color input support
    const rgbaToHexA = (rgbaStr) => {
      try {
        const m = String(rgbaStr).match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9]*\.?[0-9]+))?\)$/i);
        if (!m) return null;
        const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
        const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
        const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
        const a = m[4] != null ? Math.max(0, Math.min(1, parseFloat(m[4]))) : 1;
        const toHex = (n) => n.toString(16).padStart(2, '0');
        return { hex: `#${toHex(r)}${toHex(g)}${toHex(b)}`, alpha: a };
      } catch { return null; }
    };
    const normTrade = (node) => {
      if (!node) return;
      ['buy','sell'].forEach((k) => {
        const v = node[k];
        if (!v) return;
        if (typeof v.color === 'string' && v.color.trim().startsWith('rgba')) {
          const res = rgbaToHexA(v.color.trim());
          if (res) {
            v.color = res.hex;
            if (v.alpha == null) v.alpha = res.alpha;
          }
        } else if (typeof v.color === 'string' && v.alpha == null) {
          // Ensure alpha exists
          v.alpha = 1;
        }
      });
    };
    normTrade(merged.trade);
    return merged;
  } catch {
    return defaultPrefs;
  }
};

const ColorRow = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <span className="w-40 text-sm">{label}</span>
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-8 p-0 border rounded" />
  </div>
);

const RangeRow = ({ label, min, max, step, value, onChange, suffix = '' }) => (
  <div className="flex items-center gap-3" onPointerDown={(e) => e.currentTarget.setPointerCapture?.(e.pointerId)}>
    <span className="w-40 text-sm">{label}</span>
    {/* Use onInput to avoid blur/focus issues on small changes and keep continuous feedback */}
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onInput={(e) => onChange(Number(e.target.value))}
      onChange={(e) => onChange(Number(e.target.value))}
      className="flex-1"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    />
    <span className="w-16 text-right text-sm">{value}{suffix}</span>
  </div>
);

// Define Section at module scope to avoid remounting children on every parent render
const Section = ({ title, children, boxCls, titleCls }) => (
  <div className={`p-4 rounded-lg border ${boxCls}`}>
    <h4 className={`text-sm font-semibold mb-3 ${titleCls}`}>{title}</h4>
    <div className="space-y-3">{children}</div>
  </div>
);

export default function IndicatorSettings({ theme = 'light' }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const boxCls = theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const titleCls = theme === 'dark' ? 'text-gray-200' : 'text-gray-800';

  // Debounced auto-save to reduce flashing/closing of native pickers on every tiny change
  const save = () => {
    try {
      localStorage.setItem('borsai.indicatorStylePrefs.v1', JSON.stringify(prefs));
      // Notify charts in this tab to refresh styles
      window.dispatchEvent(new CustomEvent('borsai.indicatorStylePrefs.updated'));
    } catch {}
  };

  // Debounce write to localStorage for smoother UI while dragging sliders / tweaking colors
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem('borsai.indicatorStylePrefs.v1', JSON.stringify(prefs));
        window.dispatchEvent(new CustomEvent('borsai.indicatorStylePrefs.updated'));
      } catch {}

      // Also persist to DB for cross-device sync
      try {
        schedulePersistClientSettings({
          localStorage: {
            'borsai.indicatorStylePrefs.v1': prefs,
          },
        }, 250);
      } catch {}
    }, 200);
    return () => clearTimeout(id);
  }, [prefs]);

  const reset = () => {
    setPrefs(defaultPrefs);
    try {
      localStorage.setItem('borsai.indicatorStylePrefs.v1', JSON.stringify(defaultPrefs));
      window.dispatchEvent(new CustomEvent('borsai.indicatorStylePrefs.updated'));
    } catch {}
  };

  return (
    <div className="space-y-6">
      <Section title="Candlesticks" boxCls={boxCls} titleCls={titleCls}>
        <ColorRow label="Up Color" value={prefs.candle.upColor}
          onChange={(v) => setPrefs((prev) => ({ ...prev, candle: { ...prev.candle, upColor: v } }))} />
        <ColorRow label="Down Color" value={prefs.candle.downColor}
          onChange={(v) => setPrefs((prev) => ({ ...prev, candle: { ...prev.candle, downColor: v } }))} />
        <ColorRow label="Wick Up" value={prefs.candle.wickUpColor}
          onChange={(v) => setPrefs((prev) => ({ ...prev, candle: { ...prev.candle, wickUpColor: v } }))} />
        <ColorRow label="Wick Down" value={prefs.candle.wickDownColor}
          onChange={(v) => setPrefs((prev) => ({ ...prev, candle: { ...prev.candle, wickDownColor: v } }))} />
      </Section>

      <Section title="Simple Moving Averages (SMA)" boxCls={boxCls} titleCls={titleCls}>
        {['9', '20','50','100','200'].map((p) => (
          <div key={`sma-${p}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorRow label={`SMA ${p} Color`} value={prefs.sma[p].color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, sma: { ...prev.sma, [p]: { ...prev.sma[p], color: v } } }))} />
            <RangeRow label={`SMA ${p} Width`} min={1} max={6} step={1} value={prefs.sma[p].width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, sma: { ...prev.sma, [p]: { ...prev.sma[p], width: v } } }))} />
            <RangeRow label={`SMA ${p} Opacity`} min={0} max={1} step={0.05} value={prefs.sma[p].alpha}
              onChange={(v) => setPrefs((prev) => ({ ...prev, sma: { ...prev.sma, [p]: { ...prev.sma[p], alpha: v } } }))} />
          </div>
        ))}
      </Section>

      <Section title="Exponential Moving Averages (EMA)" boxCls={boxCls} titleCls={titleCls}>
        {['9', '20','50','100','200'].map((p) => (
          <div key={`ema-${p}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorRow label={`EMA ${p} Color`} value={prefs.ema[p].color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, ema: { ...prev.ema, [p]: { ...prev.ema[p], color: v } } }))} />
            <RangeRow label={`EMA ${p} Width`} min={1} max={6} step={1} value={prefs.ema[p].width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, ema: { ...prev.ema, [p]: { ...prev.ema[p], width: v } } }))} />
            <RangeRow label={`EMA ${p} Opacity`} min={0} max={1} step={0.05} value={prefs.ema[p].alpha}
              onChange={(v) => setPrefs((prev) => ({ ...prev, ema: { ...prev.ema, [p]: { ...prev.ema[p], alpha: v } } }))} />
          </div>
        ))}
      </Section>

      <Section title="Kaufman's Adaptive Moving Average (KAMA)" boxCls={boxCls} titleCls={titleCls}>
        {['9', '20','50','100','200'].map((p) => (
          <div key={`kama-${p}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorRow label={`KAMA ${p} Color`} value={prefs.kama[p].color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, kama: { ...prev.kama, [p]: { ...prev.kama[p], color: v } } }))} />
            <RangeRow label={`KAMA ${p} Width`} min={1} max={6} step={1} value={prefs.kama[p].width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, kama: { ...prev.kama, [p]: { ...prev.kama[p], width: v } } }))} />
            <RangeRow label={`KAMA ${p} Opacity`} min={0} max={1} step={0.05} value={prefs.kama[p].alpha}
              onChange={(v) => setPrefs((prev) => ({ ...prev, kama: { ...prev.kama, [p]: { ...prev.kama[p], alpha: v } } }))} />
          </div>
        ))}
      </Section>

      <Section title="Bollinger Bands" boxCls={boxCls} titleCls={titleCls}>
        {['upper','middle','lower'].map((k) => (
          <div key={`bb-${k}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorRow label={`${k[0].toUpperCase()+k.slice(1)} Color`} value={prefs.bb[k].color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, bb: { ...prev.bb, [k]: { ...prev.bb[k], color: v } } }))} />
            <RangeRow label={`${k[0].toUpperCase()+k.slice(1)} Width`} min={1} max={6} step={1} value={prefs.bb[k].width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, bb: { ...prev.bb, [k]: { ...prev.bb[k], width: v } } }))} />
            <RangeRow label={`${k[0].toUpperCase()+k.slice(1)} Opacity`} min={0} max={1} step={0.05} value={prefs.bb[k].alpha}
              onChange={(v) => setPrefs((prev) => ({ ...prev, bb: { ...prev.bb, [k]: { ...prev.bb[k], alpha: v } } }))} />
          </div>
        ))}
      </Section>

      <Section title="Ichimoku" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="Senkou A Color" value={prefs.ichimoku.a.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, a: { ...prev.ichimoku.a, color: v } } }))} />
          <RangeRow label="Senkou A Width" min={1} max={6} step={1} value={prefs.ichimoku.a.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, a: { ...prev.ichimoku.a, width: v } } }))} />
          <RangeRow label="Senkou A Opacity" min={0} max={1} step={0.05} value={prefs.ichimoku.a.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, a: { ...prev.ichimoku.a, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Senkou B Color" value={prefs.ichimoku.b.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, b: { ...prev.ichimoku.b, color: v } } }))} />
          <RangeRow label="Senkou B Width" min={1} max={6} step={1} value={prefs.ichimoku.b.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, b: { ...prev.ichimoku.b, width: v } } }))} />
          <RangeRow label="Senkou B Opacity" min={0} max={1} step={0.05} value={prefs.ichimoku.b.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, b: { ...prev.ichimoku.b, alpha: v } } }))} />
        </div>
        <div className="mt-3">
          <RangeRow label="Cloud Fill Opacity" min={0} max={1} step={0.02} value={prefs.ichimoku.cloudAlpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, ichimoku: { ...prev.ichimoku, cloudAlpha: v } }))} />
        </div>
      </Section>

            <Section title="RSI Bands" boxCls={boxCls} titleCls={titleCls}>
        <div className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorRow label="Lower Band Color" value={prefs.rsib.lower.color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, rsib: { ...prev.rsib, lower: { ...prev.rsib.lower, color: v } } }))} />
            <RangeRow label="Lower Width" min={1} max={6} step={1} value={prefs.rsib.lower.width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, rsib: { ...prev.rsib, lower: { ...prev.rsib.lower, width: v } } }))} />

            <ColorRow label="Mid Band Color" value={prefs.rsib.mid.color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, rsib: { ...prev.rsib, mid: { ...prev.rsib.mid, color: v } } }))} />
            <RangeRow label="Mid Width" min={1} max={6} step={1} value={prefs.rsib.mid.width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, rsib: { ...prev.rsib, mid: { ...prev.rsib.mid, width: v } } }))} />

            <ColorRow label="Upper Band Color" value={prefs.rsib.upper.color}
              onChange={(v) => setPrefs((prev) => ({ ...prev, rsib: { ...prev.rsib, upper: { ...prev.rsib.upper, color: v } } }))} />
            <RangeRow label="Upper Width" min={1} max={6} step={1} value={prefs.rsib.upper.width}
              onChange={(v) => setPrefs((prev) => ({ ...prev, rsib: { ...prev.rsib, upper: { ...prev.rsib.upper, width: v } } }))} />
          </div>
        </div>
      </Section>

      <Section title="RSI" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="RSI Line" value={prefs.rsi.line.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, rsi: { ...prev.rsi, line: { ...prev.rsi.line, color: v } } }))} />
          <RangeRow label="RSI Width" min={1} max={6} step={1} value={prefs.rsi.line.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, rsi: { ...prev.rsi, line: { ...prev.rsi.line, width: v } } }))} />
          <RangeRow label="RSI Opacity" min={0} max={1} step={0.05} value={prefs.rsi.line.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, rsi: { ...prev.rsi, line: { ...prev.rsi.line, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Guide Lines" value={prefs.rsi.guide.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, rsi: { ...prev.rsi, guide: { ...prev.rsi.guide, color: v } } }))} />
          <RangeRow label="Guide Width" min={1} max={4} step={1} value={prefs.rsi.guide.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, rsi: { ...prev.rsi, guide: { ...prev.rsi.guide, width: v } } }))} />
          <RangeRow label="Guide Opacity" min={0} max={1} step={0.05} value={prefs.rsi.guide.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, rsi: { ...prev.rsi, guide: { ...prev.rsi.guide, alpha: v } } }))} />
        </div>
      </Section>

      <Section title="MACD" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="MACD Line" value={prefs.macd.macdLine.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, macdLine: { ...prev.macd.macdLine, color: v } } }))} />
          <RangeRow label="MACD Width" min={1} max={6} step={1} value={prefs.macd.macdLine.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, macdLine: { ...prev.macd.macdLine, width: v } } }))} />
          <RangeRow label="MACD Opacity" min={0} max={1} step={0.05} value={prefs.macd.macdLine.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, macdLine: { ...prev.macd.macdLine, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Signal Line" value={prefs.macd.signalLine.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, signalLine: { ...prev.macd.signalLine, color: v } } }))} />
          <RangeRow label="Signal Width" min={1} max={6} step={1} value={prefs.macd.signalLine.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, signalLine: { ...prev.macd.signalLine, width: v } } }))} />
          <RangeRow label="Signal Opacity" min={0} max={1} step={0.05} value={prefs.macd.signalLine.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, signalLine: { ...prev.macd.signalLine, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Histogram Up" value={prefs.macd.histUp.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, histUp: { ...prev.macd.histUp, color: v } } }))} />
          <RangeRow label="Hist Up Opacity" min={0} max={1} step={0.05} value={prefs.macd.histUp.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, histUp: { ...prev.macd.histUp, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Histogram Down" value={prefs.macd.histDown.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, histDown: { ...prev.macd.histDown, color: v } } }))} />
          <RangeRow label="Hist Down Opacity" min={0} max={1} step={0.05} value={prefs.macd.histDown.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, histDown: { ...prev.macd.histDown, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Zero Line" value={prefs.macd.zeroLine.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, zeroLine: { ...prev.macd.zeroLine, color: v } } }))} />
          <RangeRow label="Zero Width" min={1} max={4} step={1} value={prefs.macd.zeroLine.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, zeroLine: { ...prev.macd.zeroLine, width: v } } }))} />
          <RangeRow label="Zero Opacity" min={0} max={1} step={0.05} value={prefs.macd.zeroLine.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, macd: { ...prev.macd, zeroLine: { ...prev.macd.zeroLine, alpha: v } } }))} />
        </div>
      </Section>

      <Section title="ADX" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="ADX Line" value={prefs.adx.adx.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, adx: { ...prev.adx.adx, color: v } } }))} />
          <RangeRow label="ADX Width" min={1} max={6} step={1} value={prefs.adx.adx.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, adx: { ...prev.adx.adx, width: v } } }))} />
          <RangeRow label="ADX Opacity" min={0} max={1} step={0.05} value={prefs.adx.adx.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, adx: { ...prev.adx.adx, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="+DI" value={prefs.adx.plus.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, plus: { ...prev.adx.plus, color: v } } }))} />
          <RangeRow label="+DI Width" min={1} max={6} step={1} value={prefs.adx.plus.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, plus: { ...prev.adx.plus, width: v } } }))} />
          <RangeRow label="+DI Opacity" min={0} max={1} step={0.05} value={prefs.adx.plus.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, plus: { ...prev.adx.plus, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="-DI" value={prefs.adx.minus.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, minus: { ...prev.adx.minus, color: v } } }))} />
          <RangeRow label="-DI Width" min={1} max={6} step={1} value={prefs.adx.minus.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, minus: { ...prev.adx.minus, width: v } } }))} />
          <RangeRow label="-DI Opacity" min={0} max={1} step={0.05} value={prefs.adx.minus.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, minus: { ...prev.adx.minus, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Guide 25" value={prefs.adx.guide25.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, guide25: { ...prev.adx.guide25, color: v } } }))} />
          <RangeRow label="Guide Width" min={1} max={4} step={1} value={prefs.adx.guide25.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, guide25: { ...prev.adx.guide25, width: v } } }))} />
          <RangeRow label="Guide Opacity" min={0} max={1} step={0.05} value={prefs.adx.guide25.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, adx: { ...prev.adx, guide25: { ...prev.adx.guide25, alpha: v } } }))} />
        </div>
      </Section>

      <Section title="Adaptive Divergences" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="Hidden Bull" value={prefs.div.hidBull.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, hidBull: { ...prev.div.hidBull, color: v } } }))} />
          <RangeRow label="Hidden Bull Opacity" min={0} max={1} step={0.05} value={prefs.div.hidBull.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, hidBull: { ...prev.div.hidBull, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Hidden Bear" value={prefs.div.hidBear.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, hidBear: { ...prev.div.hidBear, color: v } } }))} />
          <RangeRow label="Hidden Bear Opacity" min={0} max={1} step={0.05} value={prefs.div.hidBear.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, hidBear: { ...prev.div.hidBear, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Regular Bull" value={prefs.div.regBull.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, regBull: { ...prev.div.regBull, color: v } } }))} />
          <RangeRow label="Regular Bull Opacity" min={0} max={1} step={0.05} value={prefs.div.regBull.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, regBull: { ...prev.div.regBull, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Regular Bear" value={prefs.div.regBear.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, regBear: { ...prev.div.regBear, color: v } } }))} />
          <RangeRow label="Regular Bear Opacity" min={0} max={1} step={0.05} value={prefs.div.regBear.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, regBear: { ...prev.div.regBear, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Knoxville" value={prefs.div.knox.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, knox: { ...prev.div.knox, color: v } } }))} />
          <RangeRow label="Knoxville Opacity" min={0} max={1} step={0.05} value={prefs.div.knox.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, div: { ...prev.div, knox: { ...prev.div.knox, alpha: v } } }))} />
        </div>
      </Section>

      <Section title="Divergences" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="Hidden Bull" value={prefs.dvl.hidBull.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, hidBull: { ...prev.dvl.hidBull, color: v } } }))} />
          <RangeRow label="Hidden Bull Opacity" min={0} max={1} step={0.05} value={prefs.dvl.hidBull.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, hidBull: { ...prev.dvl.hidBull, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Hidden Bear" value={prefs.dvl.hidBear.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, hidBear: { ...prev.dvl.hidBear, color: v } } }))} />
          <RangeRow label="Hidden Bear Opacity" min={0} max={1} step={0.05} value={prefs.dvl.hidBear.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, hidBear: { ...prev.dvl.hidBear, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Regular Bull" value={prefs.dvl.regBull.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, regBull: { ...prev.dvl.regBull, color: v } } }))} />
          <RangeRow label="Regular Bull Opacity" min={0} max={1} step={0.05} value={prefs.dvl.regBull.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, regBull: { ...prev.dvl.regBull, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Regular Bear" value={prefs.dvl.regBear.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, regBear: { ...prev.dvl.regBear, color: v } } }))} />
          <RangeRow label="Regular Bear Opacity" min={0} max={1} step={0.05} value={prefs.dvl.regBear.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, dvl: { ...prev.dvl, regBear: { ...prev.dvl.regBear, alpha: v } } }))} />
        </div>
      </Section>

      <Section title="Predictions & Trade Signals" boxCls={boxCls} titleCls={titleCls}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorRow label="Prediction Line" value={prefs.prediction.line.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, prediction: { ...prev.prediction, line: { ...prev.prediction.line, color: v } } }))} />
          <RangeRow label="Prediction Width" min={1} max={6} step={1} value={prefs.prediction.line.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, prediction: { ...prev.prediction, line: { ...prev.prediction.line, width: v } } }))} />
          <RangeRow label="Prediction Opacity" min={0} max={1} step={0.05} value={prefs.prediction.line.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, prediction: { ...prev.prediction, line: { ...prev.prediction.line, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="Hover Pred. Line" value={prefs.prediction.hoverLine.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, prediction: { ...prev.prediction, hoverLine: { ...prev.prediction.hoverLine, color: v } } }))} />
          <RangeRow label="Hover Pred. Width" min={1} max={6} step={1} value={prefs.prediction.hoverLine.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, prediction: { ...prev.prediction, hoverLine: { ...prev.prediction.hoverLine, width: v } } }))} />
          <RangeRow label="Hover Pred. Opacity" min={0} max={1} step={0.05} value={prefs.prediction.hoverLine.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, prediction: { ...prev.prediction, hoverLine: { ...prev.prediction.hoverLine, alpha: v } } }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ColorRow label="BUY Arrow Color" value={prefs.trade.buy.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, trade: { ...prev.trade, buy: { ...prev.trade.buy, color: v } } }))} />
          <RangeRow label="BUY Opacity" min={0} max={1} step={0.05} value={prefs.trade.buy.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, trade: { ...prev.trade, buy: { ...prev.trade.buy, alpha: v } } }))} />
          <ColorRow label="SELL Arrow Color" value={prefs.trade.sell.color}
            onChange={(v) => setPrefs((prev) => ({ ...prev, trade: { ...prev.trade, sell: { ...prev.trade.sell, color: v } } }))} />
          <RangeRow label="SELL Opacity" min={0} max={1} step={0.05} value={prefs.trade.sell.alpha}
            onChange={(v) => setPrefs((prev) => ({ ...prev, trade: { ...prev.trade, sell: { ...prev.trade.sell, alpha: v } } }))} />
          <RangeRow label="Arrow Width" min={3} max={12} step={1} value={prefs.trade.width}
            onChange={(v) => setPrefs((prev) => ({ ...prev, trade: { ...prev.trade, width: v } }))} />
        </div>
      </Section>

      <div className="flex items-center gap-3">
        <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Save</button>
        <button onClick={reset} className="px-4 py-2 bg-gray-200 rounded-lg dark:bg-gray-700 dark:text-gray-200">Reset Defaults</button>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Changes apply immediately to open charts. If you don’t see them, try toggling the indicators panel or reloading the chart.
      </div>
    </div>
  );
}
